// Trainer Dashboard — /trainer/dashboard
//
// Loads exclusively through get_my_trainer_profile(). Profile edits
// go exclusively through update_my_trainer_profile() — that RPC's
// deployed signature does not accept status, code, approved_at, or
// terms fields, so there are no controls for them here; the trainer
// cannot edit them by construction, not just by UI omission.

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AppShell } from '../../components/AppShell';
import { BrandButton } from '../../components/BrandButton';
import { useSession } from '../../lib/useSession';
import {
  rpcGetMyTrainerProfile,
  rpcUpdateMyTrainerProfile,
  rpcGetMyTrainerReferralActivity,
  rpcGetMyTrainerClients,
  hasTrainerApplication,
  isUpdateSuccessOutcome,
  readTrainerOutcome,
  readTrainerStatus,
  readTrainerFirstName,
  readTrainerLastName,
  readTrainerEmail,
  readTrainerPhone,
  readTrainerBusinessName,
  readTrainerTrainingSpecialty,
  readTrainerLocation,
  readTrainerWebsiteOrSocial,
  readTrainerBio,
  readTrainerCertificationCredentials,
  readTrainerApproximateActiveClients,
  readTrainerRequestedCode,
  readTrainerCurrentCode,
  readTrainerApprovedCode,
  readTrainerCodeValidFrom,
  readTrainerCodeExpiresAt,
  readTrainerActive,
  readTrainerApprovedAt,
  readTrainerDiscountPercent,
  readTrainerMinimumCoreMeals,
  readTrainerMaxDiscountedCoreMeals,
  readReferralOutcome,
  readReferralSummaryOrders,
  readReferralSummaryMealsSold,
  readReferralActivityRows,
  readClientsOutcome,
  readClientsCurrentCount,
  readClientsRows,
} from '../../lib/trainerApplication';

const REFERRAL_WINDOW_DAYS = 30;

const inputStyle = {
  padding: '14px 16px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius)',
  background: 'var(--paper)',
  color: 'var(--ink)',
  fontSize: '16px',
  width: '100%',
};
const errStyle = { color: 'var(--danger, #b03a2e)' };

const STATUS_COPY = {
  pending: { label: 'Pending review', tone: 'var(--warning, #b8860b)', note: 'Your application is being reviewed by Eat This Fast & Fresh. We will notify you once a decision is made.' },
  approved: { label: 'Approved', tone: 'var(--success, #2e7d32)', note: 'You are an approved Eat This Fast & Fresh partner trainer.' },
  rejected: { label: 'Not approved', tone: 'var(--danger, #b03a2e)', note: 'Your application was not approved at this time.' },
  suspended: { label: 'Suspended', tone: 'var(--danger, #b03a2e)', note: 'Your trainer participation is currently suspended.' },
};

function fmtDate(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return '—'; }
}

function fmtPurchaseDate(v) {
  return v ? fmtDate(v) : 'No qualifying purchases yet';
}

function fmtOrderStatus(v) {
  return v || 'No qualifying purchases yet';
}

export default function TrainerDashboard() {
  const { session, loading: sessionLoading, configured } = useSession();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [noApplication, setNoApplication] = useState(false);
  const [profile, setProfile] = useState(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [trainingSpecialty, setTrainingSpecialty] = useState('');
  const [location, setLocation] = useState('');
  const [websiteOrSocial, setWebsiteOrSocial] = useState('');
  const [bio, setBio] = useState('');
  const [certificationCredentials, setCertificationCredentials] = useState('');
  const [approximateActiveClients, setApproximateActiveClients] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);

  const [referralLoading, setReferralLoading] = useState(false);
  const [referralError, setReferralError] = useState('');
  const [referralUnavailable, setReferralUnavailable] = useState(false);
  const [referralOrders, setReferralOrders] = useState(null);
  const [referralMealsSold, setReferralMealsSold] = useState(null);
  const [referralRows, setReferralRows] = useState([]);

  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [clientsState, setClientsState] = useState(null); // 'ok' | 'trainer_not_found' | 'trainer_inactive' | 'unavailable'
  const [clientsCount, setClientsCount] = useState(null);
  const [clientsRows, setClientsRows] = useState([]);

  const phoneDisplay = React.useMemo(() => {
    const p = session?.user?.phone;
    return p ? (p.startsWith('+') ? p : '+' + p) : '';
  }, [session]);

  function hydrateForm(payload) {
    setFirstName(readTrainerFirstName(payload) || '');
    setLastName(readTrainerLastName(payload) || '');
    setEmail(readTrainerEmail(payload) || '');
    setBusinessName(readTrainerBusinessName(payload) || '');
    setTrainingSpecialty(readTrainerTrainingSpecialty(payload) || '');
    setLocation(readTrainerLocation(payload) || '');
    setWebsiteOrSocial(readTrainerWebsiteOrSocial(payload) || '');
    setBio(readTrainerBio(payload) || '');
    setCertificationCredentials(readTrainerCertificationCredentials(payload) || '');
    const clients = readTrainerApproximateActiveClients(payload);
    setApproximateActiveClients(clients == null ? '' : String(clients));
  }

  useEffect(() => {
    if (sessionLoading || !session) return;
    let cancelled = false;
    setLoading(true);
    rpcGetMyTrainerProfile().then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setLoadError(res.error || 'We could not load your trainer profile.');
        setLoading(false);
        return;
      }
      if (!hasTrainerApplication(res.data)) {
        setNoApplication(true);
        setLoading(false);
        return;
      }
      setProfile(res.data);
      hydrateForm(res.data);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [sessionLoading, session]);

  // Referral activity — only meaningful for an approved trainer.
  // Default reporting window: last 30 days. No trainer_id/user_id is
  // ever sent; the RPC derives the trainer from auth.uid().
  useEffect(() => {
    if (!profile) return;
    const status = readTrainerStatus(profile);
    if (status !== 'approved') return;
    let cancelled = false;
    setReferralLoading(true);
    setReferralError('');
    setReferralUnavailable(false);
    const end = new Date();
    const start = new Date(end.getTime() - REFERRAL_WINDOW_DAYS * 24 * 60 * 60 * 1000);
    rpcGetMyTrainerReferralActivity({ startIso: start.toISOString(), endIso: end.toISOString() }).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setReferralError(res.error || 'We could not load your referral activity.');
        setReferralLoading(false);
        return;
      }
      const outcome = readReferralOutcome(res.data);
      if (outcome !== 'ok') {
        setReferralUnavailable(true);
        setReferralLoading(false);
        return;
      }
      setReferralOrders(readReferralSummaryOrders(res.data));
      setReferralMealsSold(readReferralSummaryMealsSold(res.data));
      setReferralRows(readReferralActivityRows(res.data));
      setReferralLoading(false);
    });
    return () => { cancelled = true; };
  }, [profile]);

  // My Clients — get_my_trainer_clients() only, no arguments. Trainer
  // identity is resolved server-side from auth.uid(). Only meaningful
  // for an approved trainer.
  useEffect(() => {
    if (!profile) return;
    const status = readTrainerStatus(profile);
    if (status !== 'approved') return;
    let cancelled = false;
    setClientsLoading(true);
    setClientsError('');
    rpcGetMyTrainerClients().then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setClientsError(res.error || "We couldn't load your clients.");
        setClientsLoading(false);
        return;
      }
      const outcome = readClientsOutcome(res.data);
      if (outcome !== 'ok') {
        setClientsState(outcome || 'unavailable');
        setClientsLoading(false);
        return;
      }
      setClientsState('ok');
      setClientsCount(readClientsCurrentCount(res.data));
      setClientsRows(readClientsRows(res.data));
      setClientsLoading(false);
    });
    return () => { cancelled = true; };
  }, [profile]);

  if (!sessionLoading && !session) {
    return <Navigate to="/sign-in" replace state={{ returnTo: '/trainer/dashboard' }} />;
  }
  if (noApplication) {
    return <Navigate to="/trainer/apply" replace />;
  }

  async function handleSave(e) {
    e?.preventDefault?.();
    setSaveError('');
    setSaveOk(false);
    setSaving(true);
    const res = await rpcUpdateMyTrainerProfile({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phoneDisplay,
      businessName: businessName.trim(),
      trainingSpecialty: trainingSpecialty.trim(),
      location: location.trim(),
      websiteOrSocial: websiteOrSocial.trim(),
      bio: bio.trim(),
      certificationCredentials: certificationCredentials.trim(),
      approximateActiveClients: approximateActiveClients === '' ? null : Number(approximateActiveClients),
    });
    setSaving(false);
    if (!res.ok) {
      setSaveError(res.error || "We couldn't save your changes. Please try again.");
      return;
    }
    const outcome = readTrainerOutcome(res.data);
    if (isUpdateSuccessOutcome(outcome)) {
      setSaveOk(true);
      return;
    }
    setSaveError("We couldn't save your changes. Please try again.");
  }

  const status = profile ? readTrainerStatus(profile) : null;
  const statusCopy = STATUS_COPY[status] || null;
  const requestedCode = profile ? readTrainerRequestedCode(profile) : null;
  const currentCode = profile ? readTrainerCurrentCode(profile) : null;
  const approvedCode = profile ? readTrainerApprovedCode(profile) : null;
  const validFrom = profile ? readTrainerCodeValidFrom(profile) : null;
  const expiresAt = profile ? readTrainerCodeExpiresAt(profile) : null;
  const active = profile ? readTrainerActive(profile) : null;
  const approvedAt = profile ? readTrainerApprovedAt(profile) : null;
  const discountPercent = profile ? readTrainerDiscountPercent(profile) : null;
  const minimumCoreMeals = profile ? readTrainerMinimumCoreMeals(profile) : null;
  const maxDiscountedCoreMeals = profile ? readTrainerMaxDiscountedCoreMeals(profile) : null;
  const activeCode = currentCode || approvedCode || null;
  const hasActiveCode = status === 'approved' && !!activeCode;

  return (
    <AppShell>
      <div className="wizard-card" data-testid="trainer-dashboard-page">
        <div className="eyebrow" data-testid="trainer-dashboard-eyebrow">Trainer Program</div>
        <h1 className="h2 mt-3" data-testid="trainer-dashboard-title">Your trainer dashboard</h1>

        {(sessionLoading || loading) && configured && (
          <p className="mt-8 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-loading">
            Loading your trainer profile…
          </p>
        )}

        {loadError && !loading && (
          <div className="mt-6 p-4" style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }} data-testid="trainer-dashboard-load-error">
            <p className="text-sm" style={errStyle}>{loadError}</p>
            <div className="mt-3">
              <BrandButton type="button" variant="outline" size="sm" onClick={() => window.location.reload()} data-testid="trainer-dashboard-retry-btn">
                Try again
              </BrandButton>
            </div>
          </div>
        )}

        {!loading && !loadError && profile && (
          <>
            {/* Status */}
            <div className="mt-6 p-4" style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper)' }} data-testid="trainer-dashboard-status-card">
              <div className="eyebrow" style={{ color: statusCopy?.tone || 'var(--ink)' }} data-testid="trainer-dashboard-status-label">
                {statusCopy?.label || status || 'Unknown status'}
                {status === 'approved' && (
                  <span className="ml-2" style={{ color: hasActiveCode ? 'var(--success, #2e7d32)' : 'var(--warning, #b8860b)' }} data-testid="trainer-dashboard-code-state-label">
                    {hasActiveCode ? '· Active code' : '· No active code'}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-status-note">
                {statusCopy?.note || ''}
              </p>

              {(status === 'pending' || status === 'rejected' || status === 'suspended') && requestedCode && (
                <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-requested-code">
                  Requested code: {requestedCode}
                </p>
              )}
            </div>

            {/* Current trainer code — backend values only, nothing calculated in React */}
            {status === 'approved' && (
              <div className="mt-6 p-4" style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper)' }} data-testid="trainer-dashboard-code-card">
                <h2 className="h3" data-testid="trainer-dashboard-code-heading">Current trainer code</h2>

                {hasActiveCode ? (
                  <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="trainer-dashboard-code-info">
                    <div>
                      <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Trainer code</div>
                      <div className="mt-1" data-testid="trainer-dashboard-current-code">{activeCode}</div>
                    </div>
                    <div>
                      <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Discount %</div>
                      <div className="mt-1" data-testid="trainer-dashboard-discount-percent">{discountPercent == null ? '—' : `${discountPercent}%`}</div>
                    </div>
                    <div>
                      <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Minimum core meals</div>
                      <div className="mt-1" data-testid="trainer-dashboard-minimum-core-meals">{minimumCoreMeals == null ? '—' : minimumCoreMeals}</div>
                    </div>
                    <div>
                      <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Maximum discounted core meals</div>
                      <div className="mt-1" data-testid="trainer-dashboard-max-discounted-core-meals">{maxDiscountedCoreMeals == null ? '—' : maxDiscountedCoreMeals}</div>
                    </div>
                    <div>
                      <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Valid from</div>
                      <div className="mt-1" data-testid="trainer-dashboard-code-valid-from">{fmtDate(validFrom)}</div>
                    </div>
                    <div>
                      <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Expiration</div>
                      <div className="mt-1" data-testid="trainer-dashboard-code-expires-at">{fmtDate(expiresAt)}</div>
                    </div>
                    <div>
                      <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Active status</div>
                      <div className="mt-1" data-testid="trainer-dashboard-active-status">{active == null ? '—' : (active ? 'Yes' : 'No')}</div>
                    </div>
                    <div>
                      <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Approved on</div>
                      <div className="mt-1" data-testid="trainer-dashboard-approved-at">{fmtDate(approvedAt)}</div>
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-no-active-code">
                    Your trainer account is approved, but no active trainer code is currently available.
                  </p>
                )}
              </div>
            )}

            {/* My Clients — get_my_trainer_clients only. Current
                attributed roster, distinct from historical Referral
                Activity below. */}
            {status === 'approved' && (
              <div className="mt-6 p-4" style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper)' }} data-testid="trainer-dashboard-clients-card">
                <h2 className="h3" data-testid="trainer-dashboard-clients-heading">My clients</h2>

                {clientsLoading && (
                  <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-clients-loading">Loading your clients…</p>
                )}
                {clientsError && !clientsLoading && (
                  <p className="mt-3 text-sm" style={errStyle} data-testid="trainer-dashboard-clients-error">{clientsError}</p>
                )}
                {!clientsLoading && !clientsError && clientsState === 'trainer_not_found' && (
                  <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-clients-not-found">
                    Your trainer account isn&rsquo;t set up yet.
                  </p>
                )}
                {!clientsLoading && !clientsError && clientsState === 'trainer_inactive' && (
                  <p className="mt-3 text-sm" style={errStyle} data-testid="trainer-dashboard-clients-inactive">
                    Your Trainer Program account is currently inactive, so client data isn&rsquo;t shown.
                  </p>
                )}
                {!clientsLoading && !clientsError && clientsState === 'unavailable' && (
                  <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-clients-unavailable">
                    My Clients is not available right now.
                  </p>
                )}

                {!clientsLoading && !clientsError && clientsState === 'ok' && (
                  <>
                    <div className="mt-4" data-testid="trainer-dashboard-clients-summary">
                      <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Current clients</div>
                      <div className="mt-1 text-lg" data-testid="trainer-dashboard-clients-count">{clientsCount == null ? 0 : clientsCount}</div>
                    </div>

                    {clientsRows.length === 0 ? (
                      <p className="mt-5 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-clients-empty">
                        No clients have been attributed to your trainer code yet.
                      </p>
                    ) : (
                      <>
                        {/* Desktop table */}
                        <div className="mt-5 hidden md:block overflow-x-auto" data-testid="trainer-dashboard-clients-table-wrap">
                          <table className="w-full text-sm" data-testid="trainer-dashboard-clients-table">
                            <thead>
                              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                                {['Client', 'Attributed', 'Code', 'Orders', 'Meals', 'First Purchase', 'Last Purchase', 'Status'].map((h) => (
                                  <th key={h} className="text-left py-2 pr-3" style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {clientsRows.map((c, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid var(--line)' }} data-testid={`trainer-dashboard-clients-row-${i}`}>
                                  <td className="py-2 pr-3">{c.client_name || '—'}</td>
                                  <td className="py-2 pr-3">{fmtDate(c.attributed_at)}</td>
                                  <td className="py-2 pr-3">{c.trainer_code || '—'}</td>
                                  <td className="py-2 pr-3">{c.qualifying_order_count ?? 0}</td>
                                  <td className="py-2 pr-3">{c.total_meals_purchased ?? 0}</td>
                                  <td className="py-2 pr-3">{fmtPurchaseDate(c.first_qualifying_purchase_at)}</td>
                                  <td className="py-2 pr-3">{fmtPurchaseDate(c.most_recent_qualifying_purchase_at)}</td>
                                  <td className="py-2 pr-3">{fmtOrderStatus(c.most_recent_qualifying_order_status)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile stacked cards */}
                        <div className="mt-5 flex flex-col gap-3 md:hidden" data-testid="trainer-dashboard-clients-cards">
                          {clientsRows.map((c, i) => (
                            <div key={i} className="p-3" style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper-2, rgba(0,0,0,0.02))' }}
                              data-testid={`trainer-dashboard-clients-card-${i}`}>
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <span className="text-sm" style={{ fontWeight: 600 }}>{c.client_name || '—'}</span>
                                <span className="text-xs" style={{ color: 'var(--text-dim)' }}>Attributed {fmtDate(c.attributed_at)}</span>
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                                <div><span style={{ color: 'var(--text-dim)' }}>Code:</span> {c.trainer_code || '—'}</div>
                                <div><span style={{ color: 'var(--text-dim)' }}>Orders:</span> {c.qualifying_order_count ?? 0}</div>
                                <div><span style={{ color: 'var(--text-dim)' }}>Meals purchased:</span> {c.total_meals_purchased ?? 0}</div>
                                <div><span style={{ color: 'var(--text-dim)' }}>Status:</span> {fmtOrderStatus(c.most_recent_qualifying_order_status)}</div>
                                <div><span style={{ color: 'var(--text-dim)' }}>First purchase:</span> {fmtPurchaseDate(c.first_qualifying_purchase_at)}</div>
                                <div><span style={{ color: 'var(--text-dim)' }}>Last purchase:</span> {fmtPurchaseDate(c.most_recent_qualifying_purchase_at)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Referral activity — get_my_trainer_referral_activity only, last 30 days */}
            {status === 'approved' && (
              <div className="mt-6 p-4" style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper)' }} data-testid="trainer-dashboard-referral-card">
                <h2 className="h3" data-testid="trainer-dashboard-referral-heading">Referral activity (last {REFERRAL_WINDOW_DAYS} days)</h2>

                {referralLoading && (
                  <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-referral-loading">Loading referral activity…</p>
                )}
                {referralError && !referralLoading && (
                  <p className="mt-3 text-sm" style={errStyle} data-testid="trainer-dashboard-referral-error">{referralError}</p>
                )}
                {referralUnavailable && !referralLoading && !referralError && (
                  <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-referral-unavailable">
                    Referral activity is not available yet.
                  </p>
                )}

                {!referralLoading && !referralError && !referralUnavailable && (
                  <>
                    <div className="mt-4 grid grid-cols-2 gap-4 max-w-sm" data-testid="trainer-dashboard-referral-summary">
                      <div>
                        <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Orders</div>
                        <div className="mt-1 text-lg" data-testid="trainer-dashboard-referral-orders">{referralOrders == null ? '—' : referralOrders}</div>
                      </div>
                      <div>
                        <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Meals sold</div>
                        <div className="mt-1 text-lg" data-testid="trainer-dashboard-referral-meals-sold">{referralMealsSold == null ? '—' : referralMealsSold}</div>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col gap-3" data-testid="trainer-dashboard-referral-activity-list">
                      {referralRows.length === 0 ? (
                        <p className="text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-dashboard-referral-empty">No referral activity in this period.</p>
                      ) : (
                        referralRows.map((row, i) => (
                          <div key={i} className="p-3" style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper-2, rgba(0,0,0,0.02))' }}
                            data-testid={`trainer-dashboard-referral-row-${i}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <span className="text-sm" style={{ fontWeight: 600 }}>{row.client_name || '—'}</span>
                              <span className="text-xs" style={{ color: 'var(--text-dim)' }}>{fmtDate(row.order_at)}</span>
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                              <div><span style={{ color: 'var(--text-dim)' }}>Code:</span> {row.trainer_code || '—'}</div>
                              <div><span style={{ color: 'var(--text-dim)' }}>Order ref:</span> {row.order_reference || '—'}</div>
                              <div><span style={{ color: 'var(--text-dim)' }}>Status:</span> {row.order_status || '—'}</div>
                              <div><span style={{ color: 'var(--text-dim)' }}>Meals sold:</span> {row.meals_sold ?? '—'}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Editable profile */}
            <form onSubmit={handleSave} className="mt-8" data-testid="trainer-dashboard-profile-form" noValidate>
              <h2 className="h3" data-testid="trainer-dashboard-profile-heading">Your profile</h2>
              <div className="grid sm:grid-cols-2 gap-6 mt-4">
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>First name</label>
                  <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                    disabled={saving} maxLength={60} className="mt-2" style={inputStyle}
                    data-testid="trainer-dashboard-first-name-input" />
                </div>
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Last name</label>
                  <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                    disabled={saving} maxLength={60} className="mt-2" style={inputStyle}
                    data-testid="trainer-dashboard-last-name-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    disabled={saving} maxLength={254} className="mt-2" style={inputStyle}
                    data-testid="trainer-dashboard-email-input" />
                </div>
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Phone (on file from sign-in)</label>
                  <input type="tel" value={readTrainerPhone(profile) ? phoneDisplay : phoneDisplay || '—'} readOnly className="mt-2"
                    style={{ ...inputStyle, background: 'var(--paper-2, rgba(0,0,0,0.03))', color: 'var(--ink-2)' }}
                    data-testid="trainer-dashboard-phone-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Business name</label>
                  <input type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                    disabled={saving} maxLength={120} className="mt-2" style={inputStyle}
                    data-testid="trainer-dashboard-business-name-input" />
                </div>
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Training specialty</label>
                  <input type="text" value={trainingSpecialty} onChange={(e) => setTrainingSpecialty(e.target.value)}
                    disabled={saving} maxLength={120} className="mt-2" style={inputStyle}
                    data-testid="trainer-dashboard-specialty-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Location</label>
                  <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
                    disabled={saving} maxLength={120} className="mt-2" style={inputStyle}
                    data-testid="trainer-dashboard-location-input" />
                </div>
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Website or social</label>
                  <input type="text" value={websiteOrSocial} onChange={(e) => setWebsiteOrSocial(e.target.value)}
                    disabled={saving} maxLength={200} className="mt-2" style={inputStyle}
                    data-testid="trainer-dashboard-website-input" />
                </div>
              </div>
              <div className="mt-6">
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Bio</label>
                <textarea value={bio} onChange={(e) => setBio(e.target.value)}
                  disabled={saving} maxLength={2000} rows={4} className="mt-2"
                  style={{ ...inputStyle, resize: 'vertical' }}
                  data-testid="trainer-dashboard-bio-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Certification credentials</label>
                  <input type="text" value={certificationCredentials} onChange={(e) => setCertificationCredentials(e.target.value)}
                    disabled={saving} maxLength={200} className="mt-2" style={inputStyle}
                    data-testid="trainer-dashboard-certifications-input" />
                </div>
                <div>
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Approximate active clients</label>
                  <input type="number" min="0" inputMode="numeric" value={approximateActiveClients}
                    onChange={(e) => setApproximateActiveClients(e.target.value)}
                    disabled={saving} className="mt-2" style={inputStyle}
                    data-testid="trainer-dashboard-active-clients-input" />
                </div>
              </div>

              {saveError && <p className="mt-6 text-sm" style={errStyle} data-testid="trainer-dashboard-save-error">{saveError}</p>}
              {saveOk && !saveError && <p className="mt-6 text-sm" style={{ color: 'var(--success, #2e7d32)' }} data-testid="trainer-dashboard-save-success">Saved.</p>}

              <div className="mt-8">
                <BrandButton type="submit" variant="primary" disabled={saving} data-testid="trainer-dashboard-save-btn">
                  {saving ? 'Saving…' : 'Save changes'}
                </BrandButton>
              </div>
            </form>
          </>
        )}
      </div>
    </AppShell>
  );
}
